import React, { forwardRef } from 'react';
import type { ButtonProps } from '@lnr-v2/ui';
import { Button } from '@lnr-v2/ui';
import styles from './AdminButton.module.css';

export interface AdminButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'danger'
    | 'success';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const AdminButton = forwardRef<HTMLButtonElement, AdminButtonProps>(
  (
    {
      variant = 'primary',
      icon,
      iconPosition = 'left',
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classes = [
      styles.adminButton,
      variant === 'danger' && styles.danger,
      variant === 'success' && styles.success,
      icon && styles.withIcon,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Use base Button for standard variants
    if (!['danger', 'success'].includes(variant)) {
      return (
        <Button
          ref={ref}
          variant={variant as ButtonProps['variant']}
          className={classes}
          {...props}
        >
          {icon && iconPosition === 'left' && (
            <span className={styles.iconLeft} aria-hidden="true">
              {icon}
            </span>
          )}
          {children}
          {icon && iconPosition === 'right' && (
            <span className={styles.iconRight} aria-hidden="true">
              {icon}
            </span>
          )}
        </Button>
      );
    }

    // Custom rendering for danger and success variants
    return (
      <Button ref={ref} variant="primary" className={classes} {...props}>
        {icon && iconPosition === 'left' && (
          <span className={styles.iconLeft} aria-hidden="true">
            {icon}
          </span>
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <span className={styles.iconRight} aria-hidden="true">
            {icon}
          </span>
        )}
      </Button>
    );
  }
);

AdminButton.displayName = 'AdminButton';
